import { useState, useEffect } from "react";
import {
  Target, CheckCircle2, XCircle, TrendingUp, Clock, BookOpen,
  AlertTriangle, Zap, BarChart3, Calendar, Layers, Award,
} from "lucide-react";
import { getSubjectAttempts, type QuestionAttempt } from "@/lib/progress";
import { useAuth } from "@/contexts/AuthContext";
import type { Subject } from "@/data/subjects";

// ── Helpers ──────────────────────────────────────────────────────────────────

function accuracyColor(pct: number) {
  if (pct >= 80) return "#22c55e";
  if (pct >= 60) return "#f59e0b";
  return "#ef4444";
}

function masteryLabel(pct: number, attempted: number): { label: string; cls: string } {
  if (attempted === 0) return { label: "Not started", cls: "bg-[#eef1f6] text-[#5a6a7e]" };
  if (pct >= 80)       return { label: "Mastered",    cls: "bg-green-50 text-green-700" };
  if (pct >= 60)       return { label: "Improving",   cls: "bg-yellow-50 text-yellow-700" };
  return                      { label: "Needs work",  cls: "bg-red-50 text-red-600" };
}

function formatSecs(s: number) {
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function AccBar({ pct, color, height = 6 }: { pct: number; color: string; height?: number }) {
  return (
    <div className="bg-[#eef1f6] rounded-full overflow-hidden" style={{ height }}>
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
  );
}

// Circular accuracy gauge
function AccuracyGauge({ pct }: { pct: number }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;
  const color = accuracyColor(pct);
  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg width="144" height="144" viewBox="0 0 144 144" className="-rotate-90">
        <circle cx="72" cy="72" r={r} fill="none" stroke="#eef1f6" strokeWidth="12" />
        <circle
          cx="72" cy="72" r={r} fill="none"
          stroke={color} strokeWidth="12"
          strokeDasharray={`${fill} ${circ}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black" style={{ color }}>{pct.toFixed(0)}%</span>
        <span className="text-xs text-[#5a6a7e] font-medium">accuracy</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  subject: Subject;
  statsBySubject: Record<string, { attempted: number; correct: number; accuracy: number }>;
  statsByTopic:   Record<string, { attempted: number; correct: number; accuracy: number }>;
}

interface SessionSummary {
  sessionId: string;
  startedAt: string;
  questions: number;
  correct: number;
  accuracy: number;
  topicIds: string[];
  timeSpent: number;
}

export function SubjectAnalytics({ subject, statsBySubject, statsByTopic }: Props) {
  const { user } = useAuth();
  const [attempts, setAttempts] = useState<QuestionAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getSubjectAttempts(user.$id, subject.id)
      .then(setAttempts)
      .finally(() => setLoading(false));
  }, [user, subject.id]);

  // ── Derived stats ─────────────────────────────────────────────────────────

  const subjectStats = statsBySubject[subject.id];
  const hasData = subjectStats && subjectStats.attempted > 0;

  // All topics in this subject
  const allTopics = subject.modules.flatMap(m =>
    m.topics.map(t => ({ ...t, moduleId: m.id, moduleTitle: m.title, moduleIcon: m.icon }))
  );

  const totalAvailableQ = allTopics.reduce((a, t) => a + (t.questionCount || 0), 0);

  // Unique questions attempted
  const uniqueQs = new Set(attempts.map(a => a.questionId)).size;
  const coveragePct = totalAvailableQ > 0 ? Math.round((uniqueQs / totalAvailableQ) * 100) : 0;

  // Total time on subject
  const totalTimeSecs = attempts.reduce((a, at) => a + (at.timeSpent || 0), 0);

  // Sessions
  const sessionMap = new Map<string, SessionSummary>();
  for (const a of attempts) {
    const sid = a.sessionId ?? "unknown";
    const ex = sessionMap.get(sid);
    if (!ex) {
      sessionMap.set(sid, {
        sessionId: sid, startedAt: a.createdAt,
        questions: 1, correct: a.isCorrect ? 1 : 0,
        accuracy: 0,
        topicIds: [a.topicId],
        timeSpent: a.timeSpent || 0,
      });
    } else {
      ex.questions += 1;
      if (a.isCorrect) ex.correct += 1;
      if (!ex.topicIds.includes(a.topicId)) ex.topicIds.push(a.topicId);
      ex.timeSpent += a.timeSpent || 0;
      if (a.createdAt < ex.startedAt) ex.startedAt = a.createdAt;
    }
  }
  const sessions: SessionSummary[] = Array.from(sessionMap.values())
    .map(s => ({ ...s, accuracy: s.questions > 0 ? Math.round((s.correct / s.questions) * 100) : 0 }))
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

  // Time per topic
  const timeByTopic: Record<string, number> = {};
  for (const a of attempts) {
    timeByTopic[a.topicId] = (timeByTopic[a.topicId] ?? 0) + (a.timeSpent || 0);
  }

  // Avg time per question
  const avgTimeSecs = attempts.length > 0
    ? Math.round(attempts.reduce((a, at) => a + (at.timeSpent || 0), 0) / attempts.length)
    : 0;

  // Recent 8 attempts for activity feed
  const recentAttempts = [...attempts].reverse().slice(0, 8);

  // Topic rows
  const topicRows = allTopics.map(t => ({
    ...t,
    stats: statsByTopic[t.id] ?? null,
    timeSecs: timeByTopic[t.id] ?? 0,
  }));
  const practiced = topicRows.filter(t => t.stats && t.stats.attempted > 0);
  const notStarted = topicRows.filter(t => !t.stats || t.stats.attempted === 0);

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!hasData && !loading) {
    return (
      <div className="bg-white border border-[#dde3ed] rounded-2xl p-10 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#1a3a6b]/10 flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="w-8 h-8 text-[#1a3a6b]" />
        </div>
        <h3 className="font-bold text-[#1a2435] text-lg mb-2">No data yet</h3>
        <p className="text-sm text-[#5a6a7e] max-w-xs mx-auto">
          Complete a practice session to see your full performance analytics here.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#1a3a6b] border-t-transparent rounded-full animate-spin mr-3" />
        <span className="text-[#5a6a7e] text-sm">Loading your analytics…</span>
      </div>
    );
  }

  // ── Full analytics ────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* ── Row 1: Accuracy gauge + key stats ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Gauge card */}
        <div className="bg-white border border-[#dde3ed] rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center gap-4">
          <AccuracyGauge pct={subjectStats?.accuracy ?? 0} />
          <div className="text-center">
            <p className="font-bold text-[#1a2435]">Overall Accuracy</p>
            <p className="text-xs text-[#5a6a7e] mt-0.5">Across all {subjectStats?.attempted ?? 0} questions</p>
          </div>
          {subjectStats && subjectStats.accuracy < 80 && (
            <div className="text-xs text-[#5a6a7e] text-center bg-[#f8f9fc] rounded-xl px-3 py-2 border border-[#dde3ed]">
              Need <strong className="text-[#1a3a6b]">{Math.ceil((0.8 * (subjectStats.attempted + 1) - subjectStats.correct))} more correct</strong> to reach 80%
            </div>
          )}
        </div>

        {/* 5 stat cards */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { icon: Target,       label: "Attempted",       value: subjectStats?.attempted ?? 0,               color: "bg-blue-50 text-blue-600" },
            { icon: CheckCircle2, label: "Correct",         value: subjectStats?.correct ?? 0,                 color: "bg-green-50 text-green-600" },
            { icon: XCircle,      label: "Incorrect",       value: (subjectStats?.attempted ?? 0) - (subjectStats?.correct ?? 0), color: "bg-red-50 text-red-500" },
            { icon: Clock,        label: "Time on Subject", value: formatSecs(totalTimeSecs),                  color: "bg-purple-50 text-purple-600" },
            { icon: BookOpen,     label: "Avg / Question",  value: `${avgTimeSecs}s`,                          color: "bg-amber-50 text-amber-600" },
            { icon: Layers,       label: "Sessions",        value: sessions.length,                            color: "bg-indigo-50 text-indigo-600" },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="bg-white border border-[#dde3ed] rounded-2xl p-4 shadow-sm">
              <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center mb-2`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-xl font-extrabold text-[#1a3a6b] leading-none mb-1">{value}</p>
              <p className="text-xs text-[#5a6a7e]">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 2: Coverage + topic mastery matrix ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Coverage */}
        <div className="bg-white border border-[#dde3ed] rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-[#1a3a6b]" />
            <h3 className="font-bold text-[#1a2435] text-sm">Question Coverage</h3>
          </div>
          <div className="flex items-end justify-between mb-2">
            <span className="text-4xl font-black text-[#1a3a6b]">{coveragePct}%</span>
            <span className="text-xs text-[#5a6a7e] text-right">
              {uniqueQs.toLocaleString()} of {totalAvailableQ.toLocaleString()} questions attempted
            </span>
          </div>
          <AccBar pct={coveragePct} color="#1a3a6b" height={10} />
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-green-50 rounded-xl p-2">
              <div className="text-lg font-bold text-green-700">{practiced.filter(t => t.stats!.accuracy >= 80).length}</div>
              <div className="text-xs text-green-600">Mastered</div>
            </div>
            <div className="bg-yellow-50 rounded-xl p-2">
              <div className="text-lg font-bold text-yellow-700">{practiced.filter(t => t.stats!.accuracy >= 60 && t.stats!.accuracy < 80).length}</div>
              <div className="text-xs text-yellow-600">Improving</div>
            </div>
            <div className="bg-[#eef1f6] rounded-xl p-2">
              <div className="text-lg font-bold text-[#5a6a7e]">{notStarted.length}</div>
              <div className="text-xs text-[#5a6a7e]">Not started</div>
            </div>
          </div>
        </div>

        {/* Mastery by module */}
        <div className="bg-white border border-[#dde3ed] rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-[#1a3a6b]" />
            <h3 className="font-bold text-[#1a2435] text-sm">Accuracy by Module</h3>
          </div>
          <div className="space-y-3">
            {subject.modules.map(m => {
              const mAttempted = m.topics.reduce((a, t) => a + (statsByTopic[t.id]?.attempted ?? 0), 0);
              const mCorrect   = m.topics.reduce((a, t) => a + (statsByTopic[t.id]?.correct   ?? 0), 0);
              const mAcc       = mAttempted > 0 ? Math.round((mCorrect / mAttempted) * 100) : 0;
              const color      = mAttempted === 0 ? "#dde3ed" : accuracyColor(mAcc);
              return (
                <div key={m.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <m.icon className="w-3.5 h-3.5 text-[#5a6a7e]" />
                      <span className="text-sm font-semibold text-[#1a2435]">{m.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {mAttempted > 0 ? (
                        <>
                          <span className="text-xs text-[#5a6a7e]">{mCorrect}/{mAttempted}</span>
                          <span className="text-sm font-bold" style={{ color }}>{mAcc}%</span>
                        </>
                      ) : (
                        <span className="text-xs text-[#5a6a7e]">Not started</span>
                      )}
                    </div>
                  </div>
                  <AccBar pct={mAttempted === 0 ? 0 : mAcc} color={color} height={7} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Row 3: Full topic breakdown table ── */}
      <div className="bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-[#dde3ed] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#1a3a6b]" />
            <h3 className="font-bold text-[#1a2435] text-sm">All Topics — Detailed Breakdown</h3>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#5a6a7e]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Mastered</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />Improving</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Needs work</span>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-[#f8f9fc] border-b border-[#dde3ed] text-xs font-semibold text-[#5a6a7e] uppercase tracking-wide">
          <span className="col-span-4">Topic</span>
          <span className="col-span-2 text-center">Attempted</span>
          <span className="col-span-2 text-center">Correct</span>
          <span className="col-span-2 text-center">Time Spent</span>
          <span className="col-span-2 text-right">Status</span>
        </div>

        <div className="divide-y divide-[#f0f2f7]">
          {topicRows
            .sort((a, b) => {
              // Sort: practicing (weakest first), then not started
              const aAcc = a.stats?.accuracy ?? 101;
              const bAcc = b.stats?.accuracy ?? 101;
              return aAcc - bAcc;
            })
            .map(topic => {
              const { label, cls } = masteryLabel(topic.stats?.accuracy ?? 0, topic.stats?.attempted ?? 0);
              const color = topic.stats ? accuracyColor(topic.stats.accuracy) : "#dde3ed";
              const attempted = topic.stats?.attempted ?? 0;
              const correct   = topic.stats?.correct   ?? 0;
              return (
                <div key={topic.id} className="px-5 py-3 hover:bg-[#f8f9fc] transition-colors">
                  <div className="grid grid-cols-12 gap-2 items-center mb-1.5">
                    <div className="col-span-4">
                      <span className="text-xs font-semibold text-[#1a2435]">{topic.name}</span>
                      <span className="block text-[10px] text-[#5a6a7e]">{topic.moduleTitle}</span>
                    </div>
                    <div className="col-span-2 text-center text-sm font-bold text-[#1a2435]">{attempted || "—"}</div>
                    <div className="col-span-2 text-center text-sm font-bold text-[#1a2435]">{attempted > 0 ? correct : "—"}</div>
                    <div className="col-span-2 text-center text-xs text-[#5a6a7e]">{topic.timeSecs > 0 ? formatSecs(topic.timeSecs) : "—"}</div>
                    <div className="col-span-2 flex justify-end">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                    </div>
                  </div>
                  {attempted > 0 && (
                    <AccBar pct={topic.stats!.accuracy} color={color} height={5} />
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* ── Row 4: Session history + recent activity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Session history */}
        <div className="bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-[#dde3ed] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1a3a6b]" />
            <h3 className="font-bold text-[#1a2435] text-sm">Session History</h3>
            <span className="ml-auto text-xs text-[#5a6a7e]">{sessions.length} total</span>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-[#5a6a7e] p-5">No sessions yet.</p>
          ) : (
            <div className="divide-y divide-[#f0f2f7] max-h-80 overflow-y-auto">
              {sessions.map((s, i) => {
                const color = accuracyColor(s.accuracy);
                // Trend vs previous session
                const prev = sessions[i + 1];
                const trend = prev ? s.accuracy - prev.accuracy : null;
                return (
                  <div key={s.sessionId} className="px-5 py-3 hover:bg-[#f8f9fc] transition-colors">
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <span className="text-xs font-bold text-[#1a2435]">
                          {formatDate(s.startedAt)}
                        </span>
                        <span className="text-[10px] text-[#5a6a7e] ml-2">
                          {s.questions} questions · {formatSecs(s.timeSpent)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {trend !== null && trend !== 0 && (
                          <span className={`text-[10px] font-bold ${trend > 0 ? "text-green-600" : "text-red-500"}`}>
                            {trend > 0 ? "▲" : "▼"} {Math.abs(trend)}%
                          </span>
                        )}
                        <span className="text-sm font-black" style={{ color }}>{s.accuracy}%</span>
                      </div>
                    </div>
                    <AccBar pct={s.accuracy} color={color} height={5} />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.topicIds.slice(0, 3).map(tid => {
                        const t = allTopics.find(x => x.id === tid);
                        return t ? (
                          <span key={tid} className="text-[10px] bg-[#eef1f6] text-[#5a6a7e] px-1.5 py-0.5 rounded">
                            {t.name}
                          </span>
                        ) : null;
                      })}
                      {s.topicIds.length > 3 && (
                        <span className="text-[10px] text-[#5a6a7e]">+{s.topicIds.length - 3} more</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent activity + weak/strong */}
        <div className="space-y-4">
          {/* Weak topics deep dive */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h3 className="font-bold text-red-700 text-sm">Focus Here First</h3>
            </div>
            {practiced.filter(t => t.stats!.accuracy < 60).length === 0 ? (
              <p className="text-xs text-red-400">No weak topics — great work!</p>
            ) : (
              <div className="space-y-2">
                {practiced
                  .filter(t => t.stats!.accuracy < 60)
                  .sort((a, b) => a.stats!.accuracy - b.stats!.accuracy)
                  .map(t => (
                    <div key={t.id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-red-700 truncate mr-2">{t.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] text-red-500">{t.stats!.correct}/{t.stats!.attempted}</span>
                          <span className="text-xs font-bold text-red-600">{t.stats!.accuracy.toFixed(0)}%</span>
                        </div>
                      </div>
                      <AccBar pct={t.stats!.accuracy} color="#ef4444" height={4} />
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Strong topics */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-green-600" />
              <h3 className="font-bold text-green-700 text-sm">Your Strengths</h3>
            </div>
            {practiced.filter(t => t.stats!.accuracy >= 80).length === 0 ? (
              <p className="text-xs text-green-500">Keep practicing — strengths coming soon!</p>
            ) : (
              <div className="space-y-2">
                {practiced
                  .filter(t => t.stats!.accuracy >= 80)
                  .sort((a, b) => b.stats!.accuracy - a.stats!.accuracy)
                  .map(t => (
                    <div key={t.id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-green-700 truncate mr-2">{t.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] text-green-500">{t.stats!.correct}/{t.stats!.attempted}</span>
                          <span className="text-xs font-bold text-green-600">{t.stats!.accuracy.toFixed(0)}%</span>
                        </div>
                      </div>
                      <AccBar pct={t.stats!.accuracy} color="#22c55e" height={4} />
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Recent 8 questions answered */}
          {recentAttempts.length > 0 && (
            <div className="bg-white border border-[#dde3ed] rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#1a3a6b]" />
                <h3 className="font-bold text-[#1a2435] text-sm">Recent Questions</h3>
              </div>
              <div className="space-y-1.5">
                {recentAttempts.map((a, i) => {
                  const t = allTopics.find(x => x.id === a.topicId);
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${a.isCorrect ? "bg-green-100" : "bg-red-100"}`}>
                        {a.isCorrect
                          ? <CheckCircle2 className="w-3 h-3 text-green-600" />
                          : <XCircle className="w-3 h-3 text-red-500" />}
                      </span>
                      <span className="text-xs text-[#1a2435] flex-1 truncate">{t?.name ?? a.topicId}</span>
                      <span className="text-[10px] text-[#5a6a7e] flex-shrink-0">{formatSecs(a.timeSpent || 0)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
